import cn from 'classnames';
import { FC } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useParams } from 'react-router';

import { FeatureFlag } from '@/shared/api/protocol-ts/model/dto_organization_pb';
import { useAppSelector, useMedia } from '@/shared/hooks';
import { gender } from '@/shared/i18n';
import { getName } from '@/shared/lib';
import { Icon, Tooltip } from '@/shared/ui';

import { organizationModel } from '@/entities/organization';
import { patientModel, useFormatPatientDateOfBirth } from '@/entities/patient';
import { userModel } from '@/entities/user';

import styles from './PatientPersonalInfo.module.scss';

interface PatientPersonalInfoProps {
  canEditPatient: boolean;
  nameClassName?: string;
  onEditPatient: () => void;
}

export const PatientPersonalInfo: FC<PatientPersonalInfoProps> = ({
  canEditPatient,
  nameClassName,
  onEditPatient,
}) => {
  const { patientID = '' } = useParams();
  const { formatMessage } = useIntl();
  const { isMobile } = useMedia();

  const userLocale = useAppSelector(userModel.selectors.selectUserLocale);

  const patient = useAppSelector(
    patientModel.selectors.selectPatientByID(patientID),
  );

  const patientDateOfBirth = patient?.PersonalData?.DateOfBirth;

  const formattedPatientAge = useFormatPatientDateOfBirth({
    patientDateOfBirth,
    pluralized: true,
  });

  const patientGender = patient?.Gender
    ? formatMessage(gender[patient.Gender])
    : '';

  const patientExternalID = `${formatMessage({
    id: 'patientInfo.externalID',
    defaultMessage: 'ID',
  })}: ${patient?.ExternalID}`;

  const patientEmail = patient?.PersonalData?.Emails[0];

  const isForbidPatientEditing = useAppSelector(
    organizationModel.selectors.selectFeatureFlag(
      FeatureFlag.FeatureFlag_Forbid_PatientEditing,
    ),
  );

  const showEditButton = canEditPatient && !isMobile && !isForbidPatientEditing;
  const showMobileEditButton =
    canEditPatient && isMobile && !isForbidPatientEditing;

  return (
    // data-ph-block: excluded entirely from PostHog session replays — this
    // block renders the patient's name, date of birth and contact details.
    <div
      className={styles.patientInfo}
      data-component="patient-personal-info"
      data-ph-block
    >
      {!isMobile && (
        <div className={styles.patientTitleWrapper}>
          <h1
            className={cn(
              styles.patientName,
              nameClassName,
              isMobile ? 'h1m' : 'h3',
            )}
          >
            {getName(
              patient?.PersonalData?.FirstName,
              patient?.PersonalData?.LastName,
              { userLocale },
            )}
          </h1>
        </div>
      )}

      <div className={cn(styles.patientInfoDetails, isMobile ? 'p1m' : 'p2')}>
        <div className={styles.patientInfoDetailsWrapper}>
          <p>{formattedPatientAge}</p>
          <p>{patientGender}</p>
          {patient?.ExternalID ? <p>{patientExternalID}</p> : null}
        </div>
        {patientEmail && !isMobile && (
          <p className={styles.patientEmail}>{patientEmail} </p>
        )}
        {showEditButton && (
          <>
            <Tooltip.Primary
              content={
                <FormattedMessage id="global.edit" defaultMessage="Edit" />
              }
            >
              <Icon
                name="pen"
                size={24}
                onClick={onEditPatient}
                className={styles.penIcon}
              />
            </Tooltip.Primary>
          </>
        )}
        {showMobileEditButton && (
          <button
            type="button"
            onClick={onEditPatient}
            className={styles.editButton}
          >
            <Icon name="pen" size={24} className={styles.penIcon} />
          </button>
        )}
      </div>
    </div>
  );
};
